import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import { genres } from '@/mocks/movies';
import { filterMovies, getAllYears, getAllMovies } from '@/utils/movie';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const genre = params.get('genre') || '';
  const year = params.get('year') || '';

  const [input, setInput] = useState(q);
  const years = getAllYears();

  useEffect(() => {
    setInput(q);
  }, [q]);

  // live search: debounce input into url
  useEffect(() => {
    if (input === q) return;
    const timer = setTimeout(() => {
      const merged: Record<string, string> = {};
      params.forEach((value, key) => {
        merged[key] = value;
      });
      if (input.trim()) merged.q = input.trim();
      else delete merged.q;
      setParams(merged, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [input, q, params, setParams]);

  const update = (next: Record<string, string | null>) => {
    const merged: Record<string, string> = {};
    params.forEach((value, key) => {
      merged[key] = value;
    });
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === '') delete merged[k];
      else merged[k] = v;
    });
    setParams(merged, { replace: true });
  };

  const results = useMemo(
    () =>
      filterMovies({
        keyword: q || undefined,
        genre: genre || undefined,
        year: year ? Number(year) : undefined,
        sort: 'popularity',
      }),
    [q, genre, year],
  );

  const hasQuery = Boolean(q || genre || year);
  const recommendations = getAllMovies().slice(0, 4);

  useEffect(() => {
    document.title = q ? `搜索「${q}」 - 李采潭电影` : '搜索影片 - 李采潭电影';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `在李采潭电影站内搜索影视作品，关键词「${q || '全部'}」，共找到 ${results.length} 个结果。`);
  }, [q, results.length]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div className="w-full">
      {/* search header */}
      <div className="w-full bg-primary-900 text-background-50">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <nav className="flex items-center flex-wrap gap-2 text-xs text-primary-200/80 mb-4">
            <Link to="/" className="hover:text-accent-300 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-300">搜索</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-6">搜索影视作品</h1>
          <form onSubmit={submit} className="w-full max-w-2xl">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影片名称、类型或剧情关键词…"
                className="w-full h-14 pl-12 pr-32 rounded-md bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        {/* simplified filters */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <p className="text-sm text-foreground-600">
            {q ? (
              <>关键词「<span className="font-semibold text-foreground-950">{q}</span>」共找到 <span className="font-semibold text-foreground-950">{results.length}</span> 个结果</>
            ) : (
              <>共 <span className="font-semibold text-foreground-950">{results.length}</span> 部作品，输入关键词开始搜索</>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={genre}
              onChange={(e) => update({ genre: e.target.value })}
              className="h-10 px-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="">全部类型</option>
              {genres.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => update({ year: e.target.value })}
              className="h-10 px-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="">全部年份</option>
              {years.map((y) => (
                <option key={y} value={y}>{y} 年</option>
              ))}
            </select>
            {hasQuery && (
              <button
                type="button"
                onClick={() => { setInput(''); setParams({}); }}
                className="h-10 px-4 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>重置
              </button>
            )}
          </div>
        </div>

        {results.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {results.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-background-100 border border-background-200 py-16 px-6 text-center">
            <span className="inline-flex w-16 h-16 rounded-full bg-background-200 items-center justify-center mb-5">
              <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950">没有找到相关作品</h2>
            <p className="text-sm text-foreground-500 mt-2">换个关键词试试，或者浏览下面的推荐影片。</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <button
                type="button"
                onClick={() => { setInput(''); setParams({}); }}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-700 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>清空搜索
              </button>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
              >
                浏览全部分类
              </Link>
            </div>

            <div className="mt-12 text-left">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4 text-center">你可能感兴趣的作品</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {recommendations.map((m) => (
                  <MovieCard key={m.id} movie={m} compact />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}